import { post } from '../../api/api';

import { GPTModel } from './gpt-models';

export type GenerateJobDescriptionResponse = {
  suggestedDescription: string;
  explanations: string;
  isLoading: boolean;
};

const handleStream = (
  response: Response,
  onData: (_: GenerateJobDescriptionResponse) => any,
  onSuccess?: (_: AiJobDescriptionLog) => any,
) => {
  if (!response.ok) {
    throw new Error('Error encountered when generating a job description. Please try again later.');
  }

  const descriptionStopWord = '[END DESCRIPTION]';
  const logStartWord = 'aiJobDescriptionLog:';

  // strings that are mutated during stream read
  let suggestedDescription = '';
  let explanations = '';

  const reader = response.body?.getReader();
  reader?.read().then(
    /**
     * Algorithm to consume streamed text from the server and invoke
     * `onData` accordingly.
     * NOTE: assumes open square brackets constitute a new token. This can
     * be empirically verified on https://platform.openai.com/tokenizer
     */
    function processStream({ done, value }) {
      // convert node buffer to human readable string
      const parsed = new TextDecoder('utf-8').decode(value);

      if (parsed.includes(logStartWord) && onSuccess) {
        onSuccess(JSON.parse(parsed.substring(logStartWord.length)));
        return;
      }

      const chunkIncludesBracket = parsed.includes('[');

      // If we haven't encountered the divider, and there is nothing
      // in the secondary buffer, then just add it to the description
      if (!chunkIncludesBracket && !explanations) {
        suggestedDescription += parsed;

        // Otherwise, if there is nothing in the buffer, then we must be
        // encountering the divider for the first time. Finish up the
        // description and start tracking the secondary buffer
      } else if (!explanations) {
        const [preBracket, postBracket] = parsed.split('[');
        suggestedDescription += preBracket;
        explanations += `[${postBracket}`;

        // If neither of the conditions above are true, then we are
        // just building the suggestions
      } else {
        explanations += parsed;
      }

      onData({
        suggestedDescription,
        // Faster way to remove the divider than using a regex
        explanations: explanations.split(descriptionStopWord).join('').trim(),
        isLoading: !done,
      });

      if (!done) {
        reader?.read().then(processStream);
      }
    },
  );
};

const catchError = (e: any, onError?: (_: string) => any) => {
  if (onError) {
    onError(e instanceof Error ? e.message : e.toString() ?? 'Unknown error');
  }
};

export const generateJobDescription = async ({
  jobId,
  onData,
  onError,
  onSuccess,
  model,
}: {
  jobId: number | string;
  onData: (_: GenerateJobDescriptionResponse) => any;
  onError?: (_: string) => any;
  onSuccess?: (_: AiJobDescriptionLog) => any;
  model?: GPTModel;
}) =>
  post(`/ai-job-description-log/generate-job-description`, {
    body: JSON.stringify({ jobId, model }),
  })
    .then((response) => handleStream(response, onData, onSuccess))
    .catch((e) => catchError(e, onError));

export const updateJobDescription = async ({
  aiJobDescriptionLogId,
  description,
  onData,
  onError,
  onSuccess,
  model,
}: {
  aiJobDescriptionLogId: number | string;
  description: string;
  onData: (_: GenerateJobDescriptionResponse) => any;
  onError?: (_: string) => any;
  onSuccess?: (_: AiJobDescriptionLog) => any;
  model?: GPTModel;
}) =>
  post(`/ai-job-description-log/${aiJobDescriptionLogId}/update-job-description`, {
    body: JSON.stringify({ model, description }),
  })
    .then((response) => handleStream(response, onData, onSuccess))
    .catch((e) => catchError(e, onError));
